// `BesideU_Admin` is the cross-tenant operator role added 2026-05-12 per slice 11
// (DESIGN §1.2 / §3.2.1 / §4.4 / §6.6). It lives in a separate `besideUAdmins`
// table — deliberately not in `users` — so the `(organization_id, email)`
// multi-tenant uniqueness invariant on `users` is never weakened. Sessions for
// this role omit the `organizationId` claim entirely; per-tenant routes refuse
// them at the auth middleware. The HR Firewall (`canAccessEmployeeChat`) still
// returns `false` for this role by construction below.
// Slice 12 (M3) — `CGC_Member` is the external clinical / legal adviser role
// (DESIGN §5.5 / §9.3). Like `BesideU_Admin`, it lives in its own table
// (`cgcMembers`) and sessions carry no `organizationId` claim. The HR Firewall
// excludes it by exclusion (only `Employee` returns true). CGC members
// cannot access tenant content; they only act on `system_prompts` rows + their
// own decision audit log.
export const ROLES = [
  'HR_Admin',
  'HR_Viewer',
  'Manager',
  'Employee',
  'BesideU_Admin',
  'CGC_Member',
] as const;
export type Role = (typeof ROLES)[number];

// Per-tenant roles only — the values that may appear in the `users.role` column.
// `BesideU_Admin` lives in `besideUAdmins`, `CGC_Member` in `cgcMembers`, so
// excluding them here is what stops callers passing them to `users` queries
// (would fail at the DB anyway since the column enum doesn't include them).
export type TenantRole = Exclude<Role, 'BesideU_Admin' | 'CGC_Member'>;

export const HR_ROLES: readonly Role[] = ['HR_Admin', 'HR_Viewer'];

export function isHrRole(role: Role): boolean {
  return HR_ROLES.includes(role);
}

// HR Firewall: roles that may NEVER access employee chat content (per DESIGN §3.3, §4.4).
// Only `Employee` is allowed — every other role, including `BesideU_Admin`, is denied
// by exclusion (DESIGN §4.4 explicit carve-out).
export function canAccessEmployeeChat(role: Role): boolean {
  return role === 'Employee';
}

// Cross-tenant predicate — true only for the BesideU operator role. Use this to
// branch session-claim narrowing (tenant session has `organizationId`; admin
// session does not).
export function isBesideUAdminRole(role: Role): boolean {
  return role === 'BesideU_Admin';
}

// Maeve mode binding — DESIGN §5.6. Three completely separate prompts and
// context-injection pipelines. Mode is bound from the JWT role in the auth
// middleware and never read from request body or message content. This is the
// central defence against mode bleed (§5.6.4).
export const MAEVE_MODES = ['HR_Maeve', 'Manager_Guidance', 'Employee_Companion'] as const;
export type MaeveMode = (typeof MAEVE_MODES)[number];

// Pure derivation. Returns `null` for `BesideU_Admin` and `CGC_Member` —
// neither has Maeve access by construction (HR Firewall + structural absence
// of the field on their session shapes). The overloads let the auth
// middleware get the narrowed `MaeveMode` return without a non-null
// assertion when it has already branched on tenant role.
export function roleToMaeveMode(role: TenantRole): MaeveMode;
export function roleToMaeveMode(role: 'BesideU_Admin' | 'CGC_Member'): null;
export function roleToMaeveMode(role: Role): MaeveMode | null;
export function roleToMaeveMode(role: Role): MaeveMode | null {
  switch (role) {
    case 'HR_Admin':
    case 'HR_Viewer':
      return 'HR_Maeve';
    case 'Manager':
      return 'Manager_Guidance';
    case 'Employee':
      return 'Employee_Companion';
    case 'BesideU_Admin':
    case 'CGC_Member':
      return null;
    default: {
      // Exhaustiveness: adding a new role to `ROLES` makes this fail to
      // compile until the mapping is updated.
      const _exhaustive: never = role;
      return _exhaustive;
    }
  }
}
